import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import rfidService, { VehicleTransferMatchingConfig } from '../../../../services/rfidService';
import { parseApiError } from '@/utils/apiFieldErrors';

interface TransferMatchingConfigFormProps {
  isEdit?: boolean;
}

const actionOptions = [
  { value: 'NONE', label: 'No Action' },
  { value: 'AUTO_CHECKOUT', label: 'Auto Checkout' },
  { value: 'AUTO_CHECKIN', label: 'Auto Checkin' },
  { value: 'NOTIFY_ONLY', label: 'Notify Only' },
];

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  description: Yup.string(),
  entry_action: Yup.string().required('Entry action is required'),
  exit_action: Yup.string().required('Exit action is required'),
  is_active: Yup.boolean(),
  validate_route: Yup.boolean(),
  notify_on_entry: Yup.boolean(),
  notify_on_exit: Yup.boolean(),
  notify_on_route_violation: Yup.boolean(),
});

const TransferMatchingConfigForm: React.FC<TransferMatchingConfigFormProps> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<Partial<VehicleTransferMatchingConfig>>({
    name: '',
    description: '',
    entry_action: 'NONE',
    exit_action: 'NONE',
    is_active: true,
    validate_route: false,
    notify_on_entry: false,
    notify_on_exit: false,
    notify_on_route_violation: false,
  });

  useEffect(() => {
    const fetchConfig = async () => {
      if (isEdit && id) {
        try {
          setLoading(true);
          const response = await rfidService.getTransferMatchingConfigById(id);
          setInitialValues({
            name: response.data.name,
            description: response.data.description || '',
            entry_action: response.data.entry_action,
            exit_action: response.data.exit_action,
            is_active: response.data.is_active,
            validate_route: response.data.validate_route,
            notify_on_entry: response.data.notify_on_entry,
            notify_on_exit: response.data.notify_on_exit,
            notify_on_route_violation: response.data.notify_on_route_violation,
          });
        } catch (error) {
          console.error('Error fetching config:', error);
          enqueueSnackbar('Failed to load configuration', { variant: 'error' });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchConfig();
  }, [isEdit, id, enqueueSnackbar]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setErrors }) => {
      try {
        setLoading(true);
        if (isEdit && id) {
          await rfidService.updateTransferMatchingConfig(id, values);
          enqueueSnackbar('Configuration updated successfully', { variant: 'success' });
        } else {
          await rfidService.createTransferMatchingConfig(values);
          enqueueSnackbar('Configuration created successfully', { variant: 'success' });
        }
        navigate('/rfid/transfer-matching-configs');
      } catch (error: unknown) {
        console.error('Error saving config:', error);
        const { fieldErrors, message } = parseApiError(error);
        if (Object.keys(fieldErrors).length > 0) {
          // Surface server-side (API) validation errors under their field, same
          // as client-side ones (Formik already marks all fields touched on submit).
          setErrors(fieldErrors);
        } else {
          enqueueSnackbar(message || 'Failed to save configuration', { variant: 'error' });
        }
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {isEdit ? 'Edit Configuration' : 'Create New Configuration'}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={loading}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="is_active"
                    name="is_active"
                    checked={formik.values.is_active}
                    onChange={formik.handleChange}
                    disabled={loading}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                multiline
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                disabled={loading}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal"
                error={formik.touched.entry_action && Boolean(formik.errors.entry_action)}
              >
                <InputLabel id="entry-action-label">Entry Action</InputLabel>
                <Select
                  labelId="entry-action-label"
                  id="entry_action"
                  name="entry_action"
                  value={formik.values.entry_action}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Entry Action"
                  disabled={loading}
                >
                  {actionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.entry_action && formik.errors.entry_action && (
                  <FormHelperText>{formik.errors.entry_action}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                margin="normal"
                error={formik.touched.exit_action && Boolean(formik.errors.exit_action)}
              >
                <InputLabel id="exit-action-label">Exit Action</InputLabel>
                <Select
                  labelId="exit-action-label"
                  id="exit_action"
                  name="exit_action"
                  value={formik.values.exit_action}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Exit Action"
                  disabled={loading}
                >
                  {actionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.exit_action && formik.errors.exit_action && (
                  <FormHelperText>{formik.errors.exit_action}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Validation and Notification Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="validate_route"
                    name="validate_route"
                    checked={formik.values.validate_route}
                    onChange={formik.handleChange}
                    disabled={loading}
                  />
                }
                label="Validate Route"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="notify_on_route_violation"
                    name="notify_on_route_violation"
                    checked={formik.values.notify_on_route_violation}
                    onChange={formik.handleChange}
                    disabled={loading || !formik.values.validate_route}
                  />
                }
                label="Notify on Route Violation"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="notify_on_entry"
                    name="notify_on_entry"
                    checked={formik.values.notify_on_entry}
                    onChange={formik.handleChange}
                    disabled={loading}
                  />
                }
                label="Notify on Entry"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="notify_on_exit"
                    name="notify_on_exit"
                    checked={formik.values.notify_on_exit}
                    onChange={formik.handleChange}
                    disabled={loading}
                  />
                }
                label="Notify on Exit"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate('/rfid/transfer-matching-configs')}
          sx={{ mr: 2 }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </Box>
    </form>
  );
};

export default TransferMatchingConfigForm;